/**
 * 观看统计组件（观看概览）- 优化版UI设计
 * 展示4个模块的统计数据：
 * 1. 观看概览（观看时长、集数、剧集数、连续观看天数）
 * 2. 积分概览（总积分、观看积分、分享积分、收藏积分）
 * 3. 最近活动
 * 4. 观看最多的剧集
 * 
 * 设计特点：
 * - 现代卡片设计，渐变背景，柔和阴影
 * - 优化字体层次结构，数字更大更醒目
 * - 精致的图标和色彩搭配
 * - 合理的间距和留白，呼吸感强
 * - 统一的圆角设计，视觉流畅
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Paper,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  OndemandVideo as OndemandVideoIcon,
  Movie as MovieIcon,
  LocalFireDepartment as FlameIcon,
  EmojiEvents as TrophyIcon,
  PlayCircle as PlayCircleIcon,
  Share as ShareIcon,
  Favorite as FavoriteIcon,
} from '@mui/icons-material';
import { getWatchOverview, formatDuration, formatWatchDate, getActivityTypeLabel, getCurrentUserId, getGuestUserId } from '../../services/watchApi';

// ==================== 统计卡片组件 - 优化版 ====================
const StatCard = ({ title, value, icon: Icon, color, subtitle, unit = '' }) => {
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        },
        background: `linear-gradient(135deg, ${color}.50 0%, white 100%)`,
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Avatar
            sx={{
              width: 52,
              height: 52,
              bgcolor: `${color}.main`,
              color: 'white',
              boxShadow: `0 4px 14px ${color}.main`,
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 26 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  fontSize: '2rem',
                  color: `${color}.dark`,
                  lineHeight: 1.2,
                }}
              >
                {value}
              </Typography>
              {unit && (
                <Typography
                  variant="body2"
                  sx={{
                    color: `${color}.main`,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                >
                  {unit}
                </Typography>
              )}
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                mt: 0.5,
                fontSize: '0.95rem',
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  mt: 0.25,
                  opacity: 0.8,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// ==================== 积分卡片组件 - 优化版 ====================
const PointCard = ({ label, value, icon: Icon, color, total = 0 }) => {
  const percent = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 2.5,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        border: 1,
        borderColor: 'grey.100',
        overflow: 'hidden',
        position: 'relative',
        transition: 'all 0.25s ease',
        bgcolor: 'white',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          borderColor: `${color}.200`,
        },
      }}
    >
      {/* 顶部彩色色带 */}
      <Box
        sx={{
          height: 4,
          background: (theme) =>
            `linear-gradient(90deg, ${theme.palette[color].light} 0%, ${theme.palette[color].main} 100%)`,
        }}
      />

      <Box sx={{ p: 2.25 }}>
        {/* 图标 + label 一行 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '10px',
              bgcolor: `${color}.50`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ color: `${color}.main`, fontSize: 18 }} />
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: '0.85rem',
            }}
          >
            {label}
          </Typography>
        </Box>

        {/* 大数字 */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1.25 }}>
          <Typography
            sx={{
              fontWeight: 800,
              color: 'grey.900',
              fontSize: '1.75rem',
              lineHeight: 1,
              letterSpacing: '-0.5px',
            }}
          >
            {value.toLocaleString()}
          </Typography>
          <Typography
            sx={{
              color: 'text.disabled',
              fontWeight: 500,
              fontSize: '0.75rem',
            }}
          >
            分
          </Typography>
        </Box>

        {/* 占比进度条 */}
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: 'text.disabled',
                fontWeight: 500,
              }}
            >
              占总积分
            </Typography>
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: `${color}.main`,
                fontWeight: 700,
              }}
            >
              {percent}%
            </Typography>
          </Box>
          <Box
            sx={{
              width: '100%',
              height: 6,
              borderRadius: 3,
              bgcolor: `${color}.50`,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: `${Math.min(percent, 100)}%`,
                height: '100%',
                borderRadius: 3,
                background: (theme) =>
                  `linear-gradient(90deg, ${theme.palette[color].light} 0%, ${theme.palette[color].main} 100%)`,
                transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

// ==================== 累计积分卡片 - 优化版 ====================
const TotalPointsCard = ({ value, icon: Icon, color, sourceCount = 0 }) => {
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        background: (theme) =>
          `linear-gradient(135deg, ${theme.palette[color].main} 0%, ${theme.palette[color].dark} 100%)`,
        boxShadow: (theme) => `0 12px 32px ${theme.palette[color].main}40`,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* 装饰背景 */}
      <Box
        sx={{
          position: 'absolute',
          right: -30,
          top: -30,
          width: 160,
          height: 160,
          borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.1)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          right: 50,
          bottom: -40,
          width: 100,
          height: 100,
          borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.08)',
        }}
      />
      {/* 微纹理光斑 */}
      <Box
        sx={{
          position: 'absolute',
          left: -20,
          bottom: -20,
          width: 80,
          height: 80,
          borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.06)',
        }}
      />

      <CardContent
        sx={{
          p: 3,
          position: 'relative',
          zIndex: 1,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          minHeight: 180,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: 'rgba(255,255,255,0.22)',
              color: 'white',
              boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <Icon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.85)',
                fontWeight: 500,
                fontSize: '0.85rem',
                letterSpacing: '0.5px',
                mb: 0.25,
              }}
            >
              累计获得积分
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  color: 'white',
                  fontSize: '2.75rem',
                  lineHeight: 1,
                  textShadow: '0 2px 8px rgba(0,0,0,0.18)',
                  letterSpacing: '-1px',
                }}
              >
                {value.toLocaleString()}
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.85)',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                }}
              >
                分
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* 底部信息条：来源覆盖度 */}
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: '1px solid rgba(255,255,255,0.18)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.85)',
              fontSize: '0.8rem',
              fontWeight: 500,
            }}
          >
            积分来源覆盖
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography
              sx={{
                color: 'white',
                fontSize: '1rem',
                fontWeight: 700,
              }}
            >
              {sourceCount}
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '0.8rem',
              }}
            >
              / 3 类
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// 活动图标映射
const getActivityIcon = (type) => {
  const icons = {
    'watch': PlayCircleIcon,
    'share': ShareIcon,
    'favorite': FavoriteIcon,
  };
  return icons[type] || PlayCircleIcon;
};

// 活动颜色映射
const getActivityColor = (type) => {
  const colors = {
    'watch': 'primary',
    'share': 'success',
    'favorite': 'error',
  };
  return colors[type] || 'primary';
};


// ==================== 主组件 - WatchStatsTab ====================
const WatchStatsTab = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statsData, setStatsData] = useState(null);

  // 获取用户ID
  const getUserId = () => {
    if (user && (user.user_id || user._id || user.id)) {
      return user.user_id || user._id || user.id;
    }
    const currentUserId = getCurrentUserId();
    if (currentUserId) {
      return currentUserId;
    }
    return getGuestUserId();
  };

  // 加载统计数据
  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const userId = getUserId();
      const result = await getWatchOverview(userId);

      if (result.success) {
        setStatsData(result.data);
      } else {
        setError(result.message || '获取统计数据失败');
      }
    } catch (err) {
      console.error('加载观看统计失败:', err);
      setError('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载
  useEffect(() => {
    loadStats();
  }, [user]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress size={40} thickness={4} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert
          severity="error"
          sx={{
            borderRadius: 3,
            p: 2,
            boxShadow: '0 2px 12px rgba(211,47,47,0.1)',
          }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadStats}
              sx={{ borderRadius: 2 }}
            >
              重新加载
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!statsData) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          暂无统计数据
        </Alert>
      </Box>
    );
  }

  const { overview, points, recent_activity, most_watched } = statsData;

  return (
    <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
      {/* ========== 头部信息 - 优化版 ========== */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          pb: 2,
          borderBottom: 2,
          borderColor: 'grey.100',
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: 'grey.900',
              mb: 0.5,
            }}
          >
            观看概览
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            记录您的观看历程与成就
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="medium"
          startIcon={<RefreshIcon />}
          onClick={loadStats}
          sx={{
            borderRadius: 2.5,
            px: 2.5,
            py: 1,
            fontWeight: 500,
            borderColor: 'grey.300',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'primary.50',
            },
          }}
        >
          刷新数据
        </Button>
      </Box>


      {/* ========== 模块1: 观看概览 - 优化版 ========== */}
      <Box sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
          <Box
            sx={{
              width: 4,
              height: 20,
              borderRadius: 2,
              bgcolor: 'primary.main',
              mr: 1.5,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'grey.900',
              fontSize: '1.1rem',
            }}
          >
            观看数据
          </Typography>
        </Box>
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="观看时长"
              value={formatDuration(overview.total_watch_time)}
              icon={ScheduleIcon}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="已看集数"
              value={overview.total_episodes}
              icon={OndemandVideoIcon}
              color="success"
              unit="集"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="已看剧集"
              value={overview.total_dramas}
              icon={MovieIcon}
              color="info"
              unit="部"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="连续观看"
              value={overview.continue_watching_days}
              icon={FlameIcon}
              color="warning"
              unit="天"
            />
          </Grid>
        </Grid>
      </Box>

      {/* ========== 模块2: 积分概览 - 优化版 ========== */}
      <Box sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
          <Box
            sx={{
              width: 4,
              height: 20,
              borderRadius: 2,
              bgcolor: 'success.main',
              mr: 1.5,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'grey.900',
              fontSize: '1.1rem',
            }}
          >
            积分概览
          </Typography>
        </Box>
        <Grid container spacing={2.5} alignItems="stretch">
          {/* 累计积分卡 - 占 5/12 宽度 */}
          <Grid item xs={12} md={5}>
            <TotalPointsCard
              value={points.total_earned}
              icon={TrophyIcon}
              color="success"
              sourceCount={
                (points.from_watch > 0 ? 1 : 0) +
                (points.from_share > 0 ? 1 : 0) +
                (points.from_favorite > 0 ? 1 : 0)
              }
            />
          </Grid>
          {/* 三个分项卡平分剩余 7/12 */}
          <Grid item xs={12} md={7}>
            <Grid container spacing={2} sx={{ height: '100%' }}>
              <Grid item xs={12} sm={4}>
                <PointCard
                  label="观看积分"
                  value={points.from_watch}
                  icon={PlayCircleIcon}
                  color="primary"
                  total={points.total_earned}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <PointCard
                  label="分享积分"
                  value={points.from_share}
                  icon={ShareIcon}
                  color="success"
                  total={points.total_earned}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <PointCard
                  label="收藏积分"
                  value={points.from_favorite}
                  icon={FavoriteIcon}
                  color="error"
                  total={points.total_earned}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>


      {/* ========== 模块3: 最近活动 - 优化版 ========== */}
      <Box sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
          <Box
            sx={{
              width: 4,
              height: 20,
              borderRadius: 2,
              bgcolor: 'info.main',
              mr: 1.5,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'grey.900',
              fontSize: '1.1rem',
            }}
          >
            最近活动
          </Typography>
        </Box>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
            border: 1,
            borderColor: 'grey.100',
            overflow: 'hidden',
          }}
        >
          {recent_activity && recent_activity.length > 0 ? (
            <List disablePadding>
              {recent_activity.slice(0, 5).map((item, index) => {
                const ActivityIcon = getActivityIcon(item.type);
                const activityColor = getActivityColor(item.type);
                return (
                  <React.Fragment key={item.id}>
                    <ListItem
                      sx={{
                        py: 2,
                        px: 2.5,
                        transition: 'background 0.2s ease',
                        '&:hover': {
                          bgcolor: 'grey.50',
                        },
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 56 }}>
                        <Avatar
                          sx={{
                            width: 44,
                            height: 44,
                            bgcolor: `${activityColor}.50`,
                            boxShadow: `0 2px 8px ${activityColor}.10`,
                          }}
                        >
                          <ActivityIcon
                            sx={{ color: `${activityColor}.main`, fontSize: 22 }}
                          />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        sx={{ my: 0 }}
                        primary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 600,
                                color: 'grey.900',
                                fontSize: '0.95rem',
                              }}
                            >
                              {item.drama_name}
                            </Typography>
                            <Chip
                              label={getActivityTypeLabel(item.type)}
                              size="small"
                              color={activityColor}
                              variant="filled"
                              sx={{
                                height: 22,
                                fontSize: '0.75rem',
                                fontWeight: 500,
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary', fontSize: '0.85rem' }}
                          >
                            {item.episode_id != null && item.episode_id !== 'all' && `第 ${item.episode_id} 集 · `}
                            {item.is_completed && '✓ 已看完 · '}
                            {!item.is_completed && item.progress > 0 && `进度 ${item.progress}% · `}
                            {formatWatchDate(item.time)}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < recent_activity.slice(0, 5).length - 1 && (
                      <Divider
                        variant="inset"
                        component="li"
                        sx={{ ml: 5, borderColor: 'grey.100' }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          ) : (
            <Box
              sx={{
                p: 5,
                textAlign: 'center',
              }}
            >
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: 'grey.100',
                  color: 'grey.400',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <PlayCircleIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Typography
                variant="body1"
                sx={{ color: 'text.secondary', fontWeight: 500 }}
              >
                暂无活动记录
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>


      {/* ========== 模块4: 观看最多的剧集 - 优化版 ========== */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
          <Box
            sx={{
              width: 4,
              height: 20,
              borderRadius: 2,
              bgcolor: 'warning.main',
              mr: 1.5,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'grey.900',
              fontSize: '1.1rem',
            }}
          >
            观看最多的剧集
          </Typography>
        </Box>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
            border: 1,
            borderColor: 'grey.100',
            overflow: 'hidden',
          }}
        >
          {most_watched && most_watched.length > 0 ? (
            <List disablePadding>
              {most_watched.map((item, index) => {
                const progressPercent =
                  item.total_episodes > 0
                    ? Math.round((item.watched_episodes / item.total_episodes) * 100)
                    : 0;

                return (
                  <React.Fragment key={item.drama_id}>
                    <ListItem
                      sx={{
                        py: 2,
                        px: 2.5,
                        transition: 'background 0.2s ease',
                        '&:hover': {
                          bgcolor: 'grey.50',
                        },
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 72 }}>
                        <Box
                          sx={{
                            position: 'relative',
                            display: 'inline-block',
                          }}
                        >
                          <Avatar
                            variant="rounded"
                            sx={{
                              width: 56,
                              height: 42,
                              borderRadius: 2,
                              bgcolor: 'grey.200',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                              overflow: 'hidden',
                            }}
                            src={item.poster}
                            alt={item.name}
                          >
                            <MovieIcon sx={{ fontSize: 24, color: 'grey.500' }} />
                          </Avatar>
                          {/* 排名徽章 */}
                          {index < 3 && (
                            <Avatar
                              sx={{
                                width: 22,
                                height: 22,
                                fontSize: 11,
                                fontWeight: 800,
                                bgcolor:
                                  index === 0
                                    ? 'warning.main'
                                    : index === 1
                                    ? 'grey.400'
                                    : 'error.light',
                                color: index === 0 ? 'white' : 'grey.800',
                                position: 'absolute',
                                top: -6,
                                right: -6,
                                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                              }}
                            >
                              {index + 1}
                            </Avatar>
                          )}
                        </Box>
                      </ListItemAvatar>
                      <ListItemText
                        sx={{ my: 0 }}
                        primary={
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              color: 'grey.900',
                              fontSize: '0.95rem',
                              mb: 1,
                            }}
                          >
                            {item.name}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary', fontSize: '0.85rem' }}
                              >
                                已看 {item.watched_episodes} 集
                                {item.total_episodes > 0 && ` / 共 ${item.total_episodes} 集`}
                              </Typography>
                              {item.watched_episodes > 0 && (
                                <Chip
                                  label={`${progressPercent}%`}
                                  size="small"
                                  color={
                                    progressPercent >= 100 ? 'success' : 'primary'
                                  }
                                  variant="filled"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                  }}
                                />
                              )}
                            </Box>
                            {item.total_episodes > 0 && (
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(progressPercent, 100)}
                                sx={{
                                  height: 4,
                                  borderRadius: 2,
                                  bgcolor: 'grey.200',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 2,
                                    bgcolor:
                                      progressPercent >= 100 ? 'success.main' : 'primary.main',
                                  },
                                }}
                              />
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < most_watched.length - 1 && (
                      <Divider
                        variant="inset"
                        component="li"
                        sx={{ ml: 5, borderColor: 'grey.100' }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          ) : (
            <Box
              sx={{
                p: 5,
                textAlign: 'center',
              }}
            >
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: 'grey.100',
                  color: 'grey.400',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <MovieIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Typography
                variant="body1"
                sx={{ color: 'text.secondary', fontWeight: 500 }}
              >
                暂无观看记录
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default WatchStatsTab;

